import React from 'react';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import TableCell from '../TableCell';
import TableRow from '../TableRow';
import { StyledTable, TableHolder, TBody, TableScroll, Thead, NoRecordFound } from './Table.styles';

function Table({
  loading,
  loadingSkeletonLength,
  columnNames,
  rowsData,
  height,
  center,
  sm,
  headSm,
  noPadding,
  width,
  responsive = true,
  alignCenter,
  spanOnTd,
  grouped,
  align,
  noPaddingBottom,
  clickable = false, // new prop: control if rows should be clickable
  onRowClick, // optional callback for click handling
  noRecordFoundMessage = 'No Record Found',
  onClick = () => { },
  noNewStyle = false,
  headerSeperator = false,
  profilePicture,
  ...props
}) {
  const isEmpty = !loading && (!rowsData || rowsData.length === 0);
  const handleRowClick = rowData => {
    if (clickable && onRowClick) {
      onRowClick(rowData);
    }
  };

  return (
    <TableHolder responsive={responsive} noPadding={noPadding}>
      <TableScroll $height={height} $noPaddingBottom={noPaddingBottom}>
        <StyledTable $width={width} responsive={responsive} $fullHeight={rowsData?.length >= 10} noNewStyle={noNewStyle} {...props}>
          <Thead responsive={responsive} noNewStyle={noNewStyle}>
            <TableRow className="table-head" responsive={responsive} noNewStyle={noNewStyle}>
              {columnNames.map((item, index) => (
                <TableCell
                  alignCenter={alignCenter}
                  responsive={responsive}
                  heading
                  key={index}
                  center={center}
                  headSm={headSm}>
                  {item}
                </TableCell>
              ))}
            </TableRow>
            {headerSeperator && (
              <TableRow className="separator" noNewStyle={noNewStyle}>
                <th colSpan={10} />
              </TableRow>
            )}
          </Thead>

          <TBody responsive={responsive}>
            {loading ? (
              Array(loadingSkeletonLength ? loadingSkeletonLength : 10)
                .fill()
                .map((_, i) => (
                  <TableRow key={i} responsive={responsive} noNewStyle={noNewStyle}>
                    {columnNames.map((__, j) => (
                      <TableCell responsive={responsive} key={j} align={align}>
                        {profilePicture && j === 0 ? (
                          <div className='profile-skeleton'>
                            <Skeleton width={20} height={20} />
                            <Skeleton width={40} height={40} circle />
                            <Skeleton width={100} height={15} />
                          </div>
                        ) :
                          <Skeleton width={100} height={15} />
                        }
                      </TableCell>
                    ))}
                  </TableRow>
                ))
            ) : isEmpty ? (
              <tr>
                <td colSpan={columnNames.length} className="text-center">
                  <NoRecordFound>{noRecordFoundMessage}</NoRecordFound>
                </td>
              </tr>
            ) : grouped ? (
              // ✅ GROUPED MODE (sequence + variants)
              rowsData?.map((seqObj, seqIndex) => (
                <React.Fragment key={seqIndex}>
                  {/* Sequence header row */}
                  <TableRow className="sequence-row" responsive={responsive}>
                    <td colSpan={columnNames?.length}>Sequence {seqObj.sequence}</td>
                  </TableRow>

                  {/* Variants under sequence */}
                  {seqObj?.variants?.map((variant, varIndex) => (
                    <TableRow key={`${seqIndex}-${varIndex}`} onClick={() => onClick(variant)} responsive={responsive} noNewStyle={noNewStyle}>
                      {/* First col = Variant Label */}
                      <TableCell
                        responsive={responsive}
                        data-th={columnNames[0]}
                        center={center}
                        sm={sm}
                        alignCenter={alignCenter}>
                        {variant?.variant_label}
                      </TableCell>

                      {/* Rest = Report cells */}
                      {variant.cells.map((val, i) => (
                        <TableCell
                          responsive={responsive}
                          data-th={columnNames[i + 1]}
                          key={i}
                          center={center}
                          sm={sm}
                          alignCenter={alignCenter}>
                          {spanOnTd ? <span>{val}</span> : val}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))}
                </React.Fragment>
              ))
            ) : (
              rowsData.map((rowObj, index) => (
                <TableRow
                  key={rowObj?.id ?? rowObj?.key ?? index}
                  noNewStyle={noNewStyle}
                  responsive={responsive}
                  className={`${clickable ? 'clickable-row' : ''} ${rowObj?.isDropdownOpen ? 'dropdown-open' : ''}`.trim()}
                  onClick={() => handleRowClick(rowObj)}
                  style={{ cursor: clickable ? 'pointer' : 'default' }}>
                  {rowObj?.cells?.map((el, i) => (
                    <TableCell
                      responsive={responsive}
                      data-th={columnNames[i]}
                      key={i}
                      center={center}
                      sm={sm}
                      align={align}>
                      {el}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TBody>
        </StyledTable>
      </TableScroll>
    </TableHolder>
  );
}

export default Table;
